/**
 * GTM/GA4 dataLayer helpers + global auto-tracking installers.
 *
 * Layered model:
 *  - Layer 1: pushGtmEvent — explicit events from app code (form success, dialog open, etc.)
 *  - Layer 2: installAutoClickTracking — global delegated click listener that fires
 *             cta_click / nav_click / phone_click / email_click / outbound_click /
 *             file_download for any <button>, <a>, or [role="button"] in the document.
 *  - Layer 3: installScrollDepthTracking + installFormFlowTracking — engagement events.
 *
 * All events are no-ops if window.dataLayer is missing or analytics consent is denied.
 */
import { analyticsAllowed } from "@/lib/consent";
import { persistGtmEvent } from "@/lib/gtmBridge";
import { isExcludedPath } from "@/lib/analytics-loader";

export type GtmParams = Record<string, string | number | boolean | undefined | null>;

const SAFE_HOSTS_INTERNAL = ["calwaste.com", "www.calwaste.com", "lovable.app", "lovableproject.com"];

function pushRaw(event: string, params: GtmParams): void {
  if (typeof window === "undefined") return;
  if (!analyticsAllowed()) return;
  // Defense in depth: never emit dataLayer / portal events from internal routes
  // (admin, auth, customer portal). Source of truth: analytics-loader.ts.
  if (isExcludedPath(window.location.pathname)) return;
  const enriched = withDefaults(params);
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  if (w.dataLayer && typeof w.dataLayer.push === "function") {
    try {
      w.dataLayer.push({ event, ...enriched });
    } catch {
      /* noop */
    }
  }
  // Persist to portal_activity_events (fire-and-forget).
  try {
    persistGtmEvent(event, enriched as Record<string, unknown>);
  } catch {
    /* noop */
  }
}

export function pushGtmEvent(event: string, params: GtmParams = {}): void {
  pushRaw(event, params);
}

function withDefaults(params: GtmParams): GtmParams {
  if (typeof window === "undefined") return params;
  return {
    page_path: window.location.pathname,
    page_location: window.location.href,
    page_title: typeof document !== "undefined" ? document.title?.slice(0, 120) : undefined,
    device: deviceCategory(),
    ...params,
  };
}

function deviceCategory(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

const PII_RE = /[\w.+-]+@[\w-]+\.[\w.-]+|\+?\d[\d\s().-]{6,}\d/g;
function sanitizeText(text: string | null | undefined, max = 80): string {
  if (!text) return "";
  return text.replace(/\s+/g, " ").replace(PII_RE, "[redacted]").trim().slice(0, max);
}

function nearestSection(el: Element | null): string | undefined {
  let cur: Element | null = el;
  while (cur && cur !== document.body) {
    const ds = (cur as HTMLElement).dataset?.section;
    if (ds) return ds;
    if (cur.tagName === "SECTION" && cur.id) return cur.id;
    if (cur.getAttribute("role") === "dialog") return "dialog";
    if (cur.tagName === "HEADER") return "header";
    if (cur.tagName === "FOOTER") return "footer";
    if (cur.tagName === "NAV") return "nav";
    cur = cur.parentElement;
  }
  return undefined;
}

function nearestGtm(el: Element | null): Record<string, string> {
  let cur: Element | null = el;
  const out: Record<string, string> = {};
  while (cur && cur !== document.body) {
    const data = (cur as HTMLElement).dataset;
    if (data) {
      if (!out.gtm && data.gtm) out.gtm = data.gtm;
      if (!out.gtmId && data.gtmId) out.gtmId = data.gtmId;
      if (!out.gtmLocation && data.gtmLocation) out.gtmLocation = data.gtmLocation;
      if (!out.gtmType && data.gtmType) out.gtmType = data.gtmType;
    }
    if (out.gtm && out.gtmId) break;
    cur = cur.parentElement;
  }
  return out;
}

function classifyLink(href: string): { event: string; type: string } {
  if (href.startsWith("tel:")) return { event: "phone_click", type: "phone" };
  if (href.startsWith("mailto:")) return { event: "email_click", type: "email" };
  if (/\.(pdf|zip|csv|xlsx?|docx?|pptx?|mp4|mp3|webp|png|jpg|jpeg)(\?|$)/i.test(href)) {
    return { event: "file_download", type: "download" };
  }
  try {
    const url = new URL(href, window.location.href);
    const isInternal = url.hostname === window.location.hostname || SAFE_HOSTS_INTERNAL.some((h) => url.hostname.endsWith(h));
    if (!isInternal) return { event: "outbound_click", type: "outbound" };
    return { event: "nav_click", type: "nav" };
  } catch {
    return { event: "cta_click", type: "cta" };
  }
}

/* ============================================================
   Layer 2: Global delegated click tracking
   ============================================================ */
export function installAutoClickTracking(): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: MouseEvent) => {
    try {
      const path = (e.composedPath?.() ?? []) as Element[];
      const target =
        path.find((n) => n instanceof HTMLElement && (n.matches("a, button, [role='button']"))) ||
        (e.target as HTMLElement | null)?.closest?.("a, button, [role='button']");
      if (!target || !(target instanceof HTMLElement)) return;
      // Internal-route exclusion is enforced centrally in pushRaw().

      const tag = target.tagName.toLowerCase();
      const text = sanitizeText(target.getAttribute("aria-label") || target.textContent || "");
      const href = (target as HTMLAnchorElement).href || "";
      const gtm = nearestGtm(target);

      let event = gtm.gtm || "cta_click";
      let type = gtm.gtmType || "cta";
      let linkUrl: string | undefined;

      if (!gtm.gtm && tag === "a" && href) {
        const cls = classifyLink(href);
        event = cls.event;
        type = cls.type;
        linkUrl = href;
      } else if (tag === "a" && href) {
        linkUrl = href;
      }

      pushRaw(event, {
        gtm_id: gtm.gtmId || target.id || undefined,
        gtm_location: gtm.gtmLocation || nearestSection(target) || undefined,
        gtm_type: type,
        element_id: target.id || undefined,
        element_type: tag,
        cta_text: text || undefined,
        link_url: linkUrl,
        section: nearestSection(target),
      });
    } catch {
      /* noop */
    }
  };
  document.addEventListener("click", handler, { capture: true, passive: true });
  return () => document.removeEventListener("click", handler, { capture: true } as any);
}

/* ============================================================
   Layer 3a: Scroll depth, 25/50/75/90% per page
   ============================================================ */
export function installScrollDepthTracking(): () => void {
  if (typeof window === "undefined") return () => {};
  let firedFor = "";
  let reached = new Set<number>();
  const thresholds = [25, 50, 75, 90];

  const reset = () => {
    firedFor = window.location.pathname + window.location.search;
    reached = new Set();
  };
  reset();

  const onScroll = () => {
    const key = window.location.pathname + window.location.search;
    if (key !== firedFor) reset();
    const doc = document.documentElement;
    const max = Math.max(doc.scrollHeight - window.innerHeight, 1);
    const pct = Math.min(100, Math.round((window.scrollY / max) * 100));
    for (const t of thresholds) {
      if (pct >= t && !reached.has(t)) {
        reached.add(t);
        pushRaw("scroll_depth", { percent: t });
      }
    }
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
}

/* ============================================================
   Layer 3b: Form lifecycle, start, attempt
   ============================================================ */
export function installFormFlowTracking(): () => void {
  if (typeof window === "undefined") return () => {};
  const startedForms = new WeakSet<HTMLFormElement>();

  const onFocusIn = (e: Event) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const form = t.closest("form") as HTMLFormElement | null;
    if (!form || startedForms.has(form)) return;
    if (!(t.matches("input, textarea, select"))) return;
    startedForms.add(form);
    const ds = form.dataset;
    pushRaw("form_start", {
      form_id: ds.gtmId || form.id || undefined,
      form_location: ds.gtmLocation || nearestSection(form) || undefined,
    });
  };

  const onSubmit = (e: Event) => {
    const form = e.target as HTMLFormElement | null;
    if (!form || form.tagName !== "FORM") return;
    const ds = form.dataset;
    pushRaw("form_submit_attempt", {
      form_id: ds.gtmId || form.id || undefined,
      form_location: ds.gtmLocation || nearestSection(form) || undefined,
    });
  };

  document.addEventListener("focusin", onFocusIn, { capture: true });
  document.addEventListener("submit", onSubmit, { capture: true });
  return () => {
    document.removeEventListener("focusin", onFocusIn, { capture: true } as any);
    document.removeEventListener("submit", onSubmit, { capture: true } as any);
  };
}
